import { sendPageViewEvent, sendPurchaseEvent } from '@actions/gtm.action';
import { availabilitySellTripSelectionSuccess } from '@actions/availability.action';
import { Inject, Injectable } from '@angular/core';
import { OversizedBagSsrs, StandardBagSsrs } from '@constants/bags.constants';
import { WINDOW } from '@constants/global-objects.constants';
import {
  GtmCurrencyCode,
  GtmEvents,
  GtmProductNames,
  GtmProdutBagVariant,
  GtmProdutCategory,
  GtmProductFareVariant,
  GtmTravelClass,
  NOT_AVAILABLE,
  PAGE_TITLE_DEFAULT,
  UNKNOWN,
  GtmTripTypes,
} from '@constants/gtm.constants';
import { MealServiceType } from '@constants/meals.constants';
import {
  N0CategoryDetail,
  PassengerTypeCode,
  PhoneNumberType,
} from '@models/booking.models';
import { MealsSsrUpdate } from '@models/cms/meals.model';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import {
  selectBookingBags,
  selectBookingBreakdown,
  selectBookingCurrency,
  selectBookingPrimaryContact,
  selectBookingAvailableMealsSsr,
  selectBookingTripBreakdown,
  selectConfirmationNumber,
  selectJourneys,
  selectPromotionCode,
  selectBookingAvailableExtrasSsr,
  selectSegments,
} from '@selectors/booking.selector';
import { selectMealSelectionsState } from '@selectors/meals.selector';
import { GtmEventService } from '@services/gtm-event/gtm-event.service';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import {
  GtmConvertionItem,
  GtmProduct,
  GtmPurchasePayload,
} from 'src/app/interfaces/gtm-interface';
import {
  selectSearchInputState,
  selectAvailabilityTripTypeState,
} from '@selectors/availability.selector';
import { TripType } from '@constants/home.constants';
import { CabinProductClass } from '@constants/cabin.constants';

@Injectable()
export class GtmEffect {
  pageViewEvent$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(sendPageViewEvent),
        map((action) => {
          this.gtmEventService.dispatch({
            event: GtmEvents.PAGE_VIEW,
            page: {
              path: action.url,
              title: this.window?.document?.title || PAGE_TITLE_DEFAULT,
            },
          });
          return action;
        }),
      );
    },
    { dispatch: false },
  );

  purchaseEvent$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(sendPurchaseEvent),
        concatLatestFrom(() => [
          this.store.select(selectConfirmationNumber),
          this.store.select(selectBookingCurrency),
          this.store.select(selectBookingBreakdown),
          this.store.select(selectBookingTripBreakdown),
          this.store.select(selectJourneys),
          this.store.select(selectBookingPrimaryContact),
          this.store.select(selectPromotionCode),
          this.store.select(selectBookingBags),
          this.store.select(selectMealSelectionsState),
          this.store.select(selectBookingAvailableMealsSsr),
          this.store.select(selectBookingAvailableExtrasSsr),
        ]),
        filter(
          ([, p, c, b, t, j, ct, , bags, ms, ams, aes]) =>
            !!p &&
            !!c &&
            !!b &&
            !!t &&
            !!j &&
            !!ct &&
            !!bags &&
            !!ms &&
            !!ams &&
            !!aes,
        ),
        map(([evt, p, c, b, t, j, ct, pc, bags, ms, ams, aes]) => {
          const extraFares: GtmProduct[] = [];
          const getDesignatorName = (jKey: string): string => {
            /* eslint-disable @typescript-eslint/no-non-null-assertion */
            const desig = j!.find((j) => j.journeyKey === jKey)?.designator;
            return desig ? `${desig.origin}-${desig.destination}` : '';
          };
          let carryOnCount = 0;
          let carryOnPrice = 0;

          let lightCheckedCount = 0;
          let lightCheckedPrice = 0;

          let standardCheckedCount = 0;
          let standardCheckedPrice = 0;

          let heavyCheckedCount = 0;
          let heavyCheckedPrice = 0;

          let musicalInstrumentCount = 0;
          let musicalInstrumentPrice = 0;

          let surfBoardCount = 0;
          let surfBoardPrice = 0;

          let bulkyBagCount = 0;
          let bulkyBagPrice = 0;

          let sportsEquipmentCount = 0;
          let sportsEquipmentPrice = 0;

          let bikeCount = 0;
          let bikePrice = 0;

          let seatsTotalAmount = 0;
          let seatsTotalCount = 0;

          let ExtrasAggData: N0CategoryDetail[] = [];

          const flightFares: GtmProduct[] = t!.journeyBreakdowns.map((jb) => {
            seatsTotalAmount += jb.categoryBreakdown.seats?.totalPrice ?? 0;

            seatsTotalCount += (jb.categoryBreakdown.seats?.details ?? [])
              .filter((d) => d.totalPrice > 0) // Ensures not including seats which are included with bundles.
              .map((d) => d.totalCount)
              .reduce((a, c) => (a += c), 0);

            ExtrasAggData = ExtrasAggData.concat(
              jb.categoryBreakdown.extras?.details
                ? jb.categoryBreakdown.extras.details.filter(
                    (d) => d?.totalPrice > 0,
                  )
                : [],
            );

            const flightId =
              j!.find((j) => j.journeyKey === jb.journeyKey)?.segments?.[0]
                ?.flightReference ?? '';

            const productClass = (
              j!.find((j) => j.journeyKey === jb.journeyKey)?.productClass ?? ''
            ).toUpperCase();

            const variant =
              GtmProductFareVariant[
                productClass as keyof typeof GtmProductFareVariant
              ];

            const quantity = jb.categoryBreakdown.fares.fareDetails
              .map((f) => f.totalCount)
              .reduce((a, c) => (a += c), 0);

            const price = `${jb.categoryBreakdown.fares.totalPrice / quantity}`;

            return {
              id: flightId,
              name: getDesignatorName(jb.journeyKey),
              category: GtmProdutCategory.FLIGHTS,
              price,
              quantity,
              variant,
            };
          });

          bags!.forEach((jb) => {
            jb.passengerBags.forEach((pb) => {
              pb.bags.forEach((ssr) => {
                switch (ssr.ssrCode) {
                  case StandardBagSsrs.CARRYON_BAG: {
                    carryOnCount += ssr.totalCount - ssr.inBundleCount;
                    carryOnPrice += ssr.totalPrice;
                    break;
                  }
                  case StandardBagSsrs.CARRYON_BAG_15: {
                    carryOnCount += ssr.totalCount - ssr.inBundleCount;
                    carryOnPrice += ssr.totalPrice;
                    break;
                  }
                  case StandardBagSsrs.LIGHT_CHECKED_BAG: {
                    lightCheckedCount += ssr.totalCount - ssr.inBundleCount;
                    lightCheckedPrice += ssr.totalPrice;
                    break;
                  }
                  case StandardBagSsrs.CLASSIC_CHECKED_BAG: {
                    standardCheckedCount += ssr.totalCount - ssr.inBundleCount;
                    standardCheckedPrice += ssr.totalPrice;
                    break;
                  }
                  case StandardBagSsrs.HEAVY_CHECKED_BAG: {
                    heavyCheckedCount += ssr.totalCount - ssr.inBundleCount;
                    heavyCheckedPrice += ssr.totalPrice;
                    break;
                  }
                  case OversizedBagSsrs.MUSICAL_INSTRUMENT: {
                    musicalInstrumentCount +=
                      ssr.totalCount - ssr.inBundleCount;
                    musicalInstrumentPrice += ssr.totalPrice;
                    break;
                  }
                  case OversizedBagSsrs.SURFBOARD: {
                    surfBoardCount += ssr.totalCount - ssr.inBundleCount;
                    surfBoardPrice += ssr.totalPrice;
                    break;
                  }
                  case OversizedBagSsrs.BULKY_BAG: {
                    bulkyBagCount += ssr.totalCount - ssr.inBundleCount;
                    bulkyBagPrice += ssr.totalPrice;
                    break;
                  }
                  case OversizedBagSsrs.SPORTS_EQUIPMENT: {
                    sportsEquipmentCount += ssr.totalCount - ssr.inBundleCount;
                    sportsEquipmentPrice += ssr.totalPrice;
                    break;
                  }
                  case OversizedBagSsrs.BIKE: {
                    bikeCount += ssr.totalCount - ssr.inBundleCount;
                    bikePrice += ssr.totalPrice;
                    break;
                  }
                }
              });
            });
          });

          /* istanbul ignore else */
          if (carryOnCount > 0) {
            extraFares.push({
              name: GtmProductNames.BAGS,
              category: GtmProdutCategory.EXTRAS,
              variant: GtmProdutBagVariant.CB10,
              price: `${carryOnPrice / carryOnCount}`,
              quantity: carryOnCount,
            });
          }

          /* istanbul ignore else */
          if (lightCheckedCount > 0) {
            extraFares.push({
              name: GtmProductNames.BAGS,
              category: GtmProdutCategory.EXTRAS,
              variant: GtmProdutBagVariant.BA15,
              price: `${lightCheckedPrice / lightCheckedCount}`,
              quantity: lightCheckedCount,
            });
          }

          /* istanbul ignore else */
          if (standardCheckedCount > 0) {
            extraFares.push({
              name: GtmProductNames.BAGS,
              category: GtmProdutCategory.EXTRAS,
              variant: GtmProdutBagVariant.BA23,
              price: `${standardCheckedPrice / standardCheckedCount}`,
              quantity: standardCheckedCount,
            });
          }

          /* istanbul ignore else */
          if (heavyCheckedCount > 0) {
            extraFares.push({
              name: GtmProductNames.BAGS,
              category: GtmProdutCategory.EXTRAS,
              variant: GtmProdutBagVariant.BA32,
              price: `${heavyCheckedPrice / heavyCheckedCount}`,
              quantity: heavyCheckedCount,
            });
          }

          /* istanbul ignore else */
          if (musicalInstrumentCount > 0) {
            extraFares.push({
              name: GtmProductNames.BAGS,
              category: GtmProdutCategory.EXTRAS,
              variant: GtmProdutBagVariant.MUSC,
              price: `${musicalInstrumentPrice / musicalInstrumentCount}`,
              quantity: musicalInstrumentCount,
            });
          }

          /* istanbul ignore else */
          if (surfBoardCount > 0) {
            extraFares.push({
              name: GtmProductNames.BAGS,
              category: GtmProdutCategory.EXTRAS,
              variant: GtmProdutBagVariant.SURF,
              price: `${surfBoardPrice / surfBoardCount}`,
              quantity: surfBoardCount,
            });
          }

          /* istanbul ignore else */
          if (bulkyBagCount > 0) {
            extraFares.push({
              name: GtmProductNames.BAGS,
              category: GtmProdutCategory.EXTRAS,
              variant: GtmProdutBagVariant.BULK,
              price: `${bulkyBagPrice / bulkyBagCount}`,
              quantity: bulkyBagCount,
            });
          }

          /* istanbul ignore else */
          if (sportsEquipmentCount > 0) {
            extraFares.push({
              name: GtmProductNames.BAGS,
              category: GtmProdutCategory.EXTRAS,
              variant: GtmProdutBagVariant.SPEQ,
              price: `${sportsEquipmentPrice / sportsEquipmentCount}`,
              quantity: sportsEquipmentCount,
            });
          }

          /* istanbul ignore else */
          if (bikeCount > 0) {
            extraFares.push({
              name: GtmProductNames.BAGS,
              category: GtmProdutCategory.EXTRAS,
              variant: GtmProdutBagVariant.BIKE,
              price: `${bikePrice / bikeCount}`,
              quantity: bikeCount,
            });
          }

          /* istanbul ignore else */
          if (seatsTotalAmount) {
            extraFares.push({
              name: GtmProductNames.SEATS,
              category: GtmProdutCategory.EXTRAS,
              variant: NOT_AVAILABLE,
              price: `${seatsTotalAmount / seatsTotalCount}`, // Won't be accurate since we can't differentiate type of seats selected.
              quantity: seatsTotalCount,
            });
          }
          let MealsSelected: MealsSsrUpdate[] = [];
          ms!.forEach((j) => {
            j.passengers.forEach((p) => {
              const filtered = p.ssrs.filter((s) => s.included === false);
              MealsSelected = MealsSelected.concat(filtered);
            });
          });
          const UniqueSelectedMealSsrs = MealsSelected.map(
            (m) => m.ssrCode,
          ).filter((item, i, ar) => ar.indexOf(item) === i);
          UniqueSelectedMealSsrs.forEach((s) => {
            const MealsFilterByType = MealsSelected.filter(
              (m) => m.ssrCode === s,
            );
            /* istanbul ignore else */
            if (MealsFilterByType.length > 0) {
              const variant =
                ams.find((s) => s.ssrCode === MealsFilterByType[0]?.ssrCode)
                  ?.name ?? UNKNOWN;
              extraFares.push({
                name:
                  MealsFilterByType[0]?.serviceType ===
                  MealServiceType.FIRST_SERVICE
                    ? GtmProductNames.MEAL_SERVICE_ONE
                    : GtmProductNames.MEAL_SERVICE_TWO,
                category: GtmProdutCategory.EXTRAS,
                price: `${MealsFilterByType[0]?.price ?? 0}`,
                quantity: MealsFilterByType.length,
                variant,
              });
            }
          });

          /* istanbul ignore else */
          if (ExtrasAggData.length > 0) {
            const UniqueSelectedExtrasSsrs = ExtrasAggData.map(
              (e) => e.code,
            ).filter((item, i, ar) => ar.indexOf(item) === i);
            UniqueSelectedExtrasSsrs.forEach((s) => {
              const ExtrasFilterByType = ExtrasAggData.filter(
                (e) => e.code === s,
              );
              /* istanbul ignore else */
              if (ExtrasFilterByType.length > 0) {
                let totalPrice = 0;
                let totalQuantity = 0;
                ExtrasFilterByType.forEach((e) => {
                  totalPrice += e.totalPrice;
                  totalQuantity += e.totalCount;
                });
                extraFares.push({
                  name:
                    aes.find((s) => s.ssrCode === ExtrasFilterByType[0]?.code)
                      ?.name ?? UNKNOWN,
                  category: GtmProdutCategory.EXTRAS,
                  price:
                    totalQuantity > 0
                      ? (totalPrice / totalQuantity).toFixed(2)
                      : '',
                  quantity: totalQuantity,
                });
              }
            });
          }
          const purchasePayload: GtmPurchasePayload = {
            event: GtmEvents.PURCHASE,
            email: ct!.emailAddress,
            tel:
              ct!.phoneNumbers.find((p) => p.type === PhoneNumberType.Home)
                ?.number ?? '',
            ecommerce: {
              currencyCode: <GtmCurrencyCode>c,
              purchase: {
                actionField: {
                  id: p!,
                  coupon: pc,
                  revenue: `${b!.totalAmount}`,
                  tax: `${b!.journeyTotals.totalTax}`,
                },
                products: flightFares.concat(extraFares),
              },
            },
          };
          this.gtmEventService.dispatch(purchasePayload);
          return evt;
          /* eslint-disable @typescript-eslint/no-non-null-assertion */
        }),
      );
    },
    { dispatch: false },
  );

  googlePriceAccuracyEvent$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(availabilitySellTripSelectionSuccess),
        concatLatestFrom(() => [
          this.store.select(selectBookingCurrency),
          this.store.select(selectBookingBreakdown),
        ]),
        filter(([, c, bd]) => !!c && !!bd),
        map(([a, c, bd]) => {
          this.gtmEventService.dispatch({
            event: GtmEvents.PRICE_ACCURACY,
            price: {
              value: `${bd!.totalAmount}`,
              currency: <GtmCurrencyCode>c,
            },
          });
          return a;
        }),
      );
    },
    { dispatch: false },
  );

  googleFlightConversionEvent$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(sendPurchaseEvent),
        concatLatestFrom(() => [
          this.store.select(selectAvailabilityTripTypeState),
          this.store.select(selectSearchInputState),
          this.store.select(selectBookingCurrency),
          this.store.select(selectBookingBreakdown),
          this.store.select(selectSegments),
        ]),
        filter(
          ([, tt, si, bc, bb, ss]) => !!tt && !!si && !!bc && !!bb && !!ss,
        ),
        map(([ac, tt, si, bc, bb, ss]) => {
          /* eslint-disable @typescript-eslint/no-non-null-assertion */
          const items: GtmConvertionItem[] = [];
          const trip_type =
            tt === TripType.oneWay
              ? GtmTripTypes.ONEWAY
              : tt === TripType.roundTrip
              ? GtmTripTypes.ROUNDTRIP
              : GtmTripTypes.OTHER;
          const passengers_total = si!.passengers
            .map((p) => p.count)
            .reduce((p, c) => (c = c + p), 0);
          const passengers_adult =
            si!.passengers.find((p) => p.type === PassengerTypeCode.ADT)
              ?.count ?? 0;
          const passengers_child =
            si!.passengers.find((p) => p.type === PassengerTypeCode.CHD)
              ?.count ?? 0;
          const passengers_infant_in_lap =
            si!.passengers.find((p) => p.type === PassengerTypeCode.INF)
              ?.count ?? 0;
          const passengers_infant_in_seat = 0;

          ss!.forEach((s) => {
            const start_date = s.designator.departure.split('T')[0];
            if (!start_date) {
              return;
            }

            const flight_number = `${s.identifier.carrierCode}${s.identifier.identifier}`;
            const travel_class =
              s.productClass === CabinProductClass.ECONOMY_CLASSIC ||
              s.productClass === CabinProductClass.ECONOMY_LIGHT
                ? GtmTravelClass.ECONOMY
                : s.productClass === CabinProductClass.ECONOMY_PLUS
                ? GtmTravelClass.PREMIUMECONOMY
                : s.productClass === CabinProductClass.PREMIUM_CLASSIC ||
                  s.productClass === CabinProductClass.PREMIUM_LIGHT
                ? GtmTravelClass.BUSINESS
                : GtmTravelClass.FIRST;

            items.push({
              travel_class,
              booking_code: s.fares?.[0]?.travelClassCode ?? '',
              origin: s.designator.origin,
              destination: s.designator.destination,
              fare_product: GtmProductFareVariant[
                s.productClass
              ] as GtmProductFareVariant,
              flight_number,
              start_date,
            });
          });

          this.gtmEventService.dispatch({
            event: GtmEvents.FLIGHT_CONVERSION,
            flightConversion: {
              value: `${bb!.totalAmount}`,
              currency: GtmCurrencyCode[bc!] as GtmCurrencyCode,
              trip_type,
              passengers_total,
              passengers_adult,
              passengers_child,
              passengers_infant_in_seat,
              passengers_infant_in_lap,
              items,
            },
          });

          return ac;
          /* eslint-enable @typescript-eslint/no-non-null-assertion */
        }),
      );
    },
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private gtmEventService: GtmEventService,
    @Inject(WINDOW) private window: Window,
    private store: Store,
  ) {}
}
